import { COLUMNS_HIDDEN_BY_DEFAULT } from 'common/constants/analyst-dashboards';
import { EnhancedTableHeadColumn } from 'dashboard409a/components/ValuationsTable/ValuationsTable.types';
import { Logger } from 'utilities/logger';

const COLUMN_VISIBILITY_PREFIX = 'valuations_column_visibility_';

/**
 * Gets the localStorage key for a specific section
 * @param sectionId - The section identifier (e.g., 'current-jobs', 'past-jobs', 'opportunities')
 * @returns The localStorage key
 */
const getStorageKey = (sectionId: string): string => `${COLUMN_VISIBILITY_PREFIX}${sectionId}`;

/**
 * Gets the initial column visibility from localStorage or returns defaults
 * @param sectionId - The section identifier
 * @param columns - Array of all columns
 * @returns Record of column visibility states
 */
export const getInitialColumnVisibilityFromStorage = (
  sectionId: string,
  columns: EnhancedTableHeadColumn[]
): Record<string, boolean> => {
  const storageKey = getStorageKey(sectionId);
  const stored = localStorage.getItem(storageKey);
  
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      const initialVisibility: Record<string, boolean> = {};
      columns.forEach(col => {
        if (parsed[col.id] !== undefined) {
          initialVisibility[col.id] = parsed[col.id];
        } else {
          initialVisibility[col.id] = !COLUMNS_HIDDEN_BY_DEFAULT.includes(col.id);
        }
      });
      return initialVisibility;
    } catch {
      // If parsing fails, return defaults
    }
  }
  
  const initialVisibility: Record<string, boolean> = {};
  columns.forEach(col => {
    initialVisibility[col.id] = !COLUMNS_HIDDEN_BY_DEFAULT.includes(col.id);
  });
  return initialVisibility;
};

/**
 * Saves column visibility to localStorage using the setValue function from useLocalStorage hook
 * @param sectionId - The section identifier
 * @param columnVisibility - Record of column visibility states
 * @param setValue - The setValue function from useLocalStorage hook
 */
export const saveColumnVisibilityToStorage = (
  sectionId: string,
  columnVisibility: Record<string, boolean>,
  setValue: (value: Record<string, boolean>) => void
): void => {
  try {
    setValue(columnVisibility);
  } catch (error) {
    Logger.debug(`Error saving column visibility to localStorage for ${sectionId}:`, error);
  }
};

